import {
  constants as subscriptionManagerConstants,
  selectors as subscriptionManagerSelectors,
  verifiers as subscriptionManagerVerifiers,
} from '@yola/subscription-manager-js';
import { i18next, integration, site, dialogs as wsDialogs } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getPublishUrl from 'src/js/modules/editor/helpers/get-publish-url';
import wsEditorPublishingActionTypes from 'src/js/modules/publishing/constants/action-types';
import auth from 'yola-editor/src/js/modules/auth';
import dialogs from 'yola-editor/src/js/modules/dialogs';
import setSettings from 'yola-editor/src/js/modules/editor/actions/set-settings';
import featureFlagsService from 'yola-editor/src/js/modules/feature-flags/service-client';
import publishing from 'yola-editor/src/js/modules/publishing';
import publishingServiceClient from 'yola-editor/src/js/modules/publishing-service-client/service-client';
import hasSiteCustomDomain from 'yola-editor/src/js/modules/publishing/verifiers/has-site-custom-domain';
import yolaEditorSite from 'yola-editor/src/js/modules/site';
import { PUBLISHING } from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import isFreeDomainAvailable from 'yola-editor/src/js/modules/upsell/helpers/is-free-domain-available';
import user from 'yola-editor/src/js/modules/user';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import actions from '../actions';
import constants from '../constants';
import actionTypes from '../constants/action-types';
import publishingJobStatuses from '../constants/publishing-job-statuses';
import getPublishingJobsInProgress from '../helpers/get-publishing-jobs-in-progress';
import pollGetPublishingJob from '../helpers/poll-get-publishing-job';
import selectors from '../selectors';
import thunks from '../thunks';
import continuePublishingProgress from '../thunks/continue-publishing-progress';

const {
  SubscriptionType: { B2C_HOSTING_FREE },
} = subscriptionManagerConstants;
const {
  getActiveSubscriptions,
  getSubscriptionList,
  getActiveHostingSubscription,
  getCurrentPackageTrialStatus,
} = subscriptionManagerSelectors;
const { platformComponents } = user.constants;
const {
  constants: { triggerIds },
} = segment;

const websitePublisher = (store) => (next) => async (action) => {
  const service = publishingServiceClient.get();
  switch (action.type) {
    case wsEditorPublishingActionTypes.INITIATE_PUBLISH_WEBSITE: {
      const state = store.getState();
      const publishingUpsell = integration.selectors.getUpsells(state).publishing;
      store.dispatch(yolaEditorSite.actions.setSiteName(site.selectors.getSiteName(state)));

      const featureFlagsManager = featureFlagsService.get();

      const {
        publish_upsell: isPublishUpsellEnabled,
        upgrade_to_publish: isUpgradeToPublishEnabled,
        free_trial_upsell: isFreeTrialUpsellEnabled,
        loopia_trial_publish_upgrade: isLoopiaTrialPublishUpgradeEnabled,
      } = await featureFlagsManager.request([
        'publish_upsell',
        'upgrade_to_publish',
        'free_trial_upsell',
        'loopia_trial_publish_upgrade',
      ]);

      const isWL = user.selectors.getWhiteLabelStatus(state);
      const hasTrialPackage = Boolean(getCurrentPackageTrialStatus(state));
      const shouldShowLoopiaTrialUpgrade =
        isLoopiaTrialPublishUpgradeEnabled && isWL && hasTrialPackage;

      if (shouldShowLoopiaTrialUpgrade) {
        const { purchaseFlowType, premiumFeature, captions, onUpgrade } = publishingUpsell;
        store.dispatch(
          wsDialogs.actions.show(dialogTypes.UPGRADE, {
            purchaseFlowType,
            premiumFeature,
            captions,
            onUpgrade,
            onUpgradeResolve: () => {
              store.dispatch(actions.publishWebsite());
              next(action);
            },
          })
        );

        break;
      }

      const availablePlatformComponents = user.selectors.getAvailablePlatformComponents(state);

      if (!availablePlatformComponents.includes(platformComponents.DOMAINS)) {
        store.dispatch(actions.publishWebsite());

        next(action);
        break;
      }

      let userPreferences = user.selectors.getUserPreferences(state);

      if (!Number(userPreferences.isEmailVerified)) {
        try {
          userPreferences = await user.operations.fetchUserPreferences();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
        }

        if (!Number(userPreferences.isEmailVerified)) {
          store.dispatch(dialogs.actions.show(dialogs.dialogTypes.VERIFY_EMAIL));

          next(action);
          break;
        }
      }

      const siteURL = yolaEditorSite.selectors.getSiteURL(state);
      const isPublishedToCustomDomain = hasSiteCustomDomain(state);
      const activeHostingSubscription = getActiveHostingSubscription(state);
      const { domain, isThirdPartyDomain } = action.payload;
      const hasAnyDomainToPublish = domain || siteURL;
      const isCustomDomainWithoutSubscription =
        isPublishedToCustomDomain && !activeHostingSubscription;

      const isFreeTrialUpsellFlowAvailable =
        isFreeTrialUpsellEnabled && (!hasAnyDomainToPublish || isCustomDomainWithoutSubscription);

      if (isFreeTrialUpsellFlowAvailable) {
        if (activeHostingSubscription) {
          store.dispatch(
            dialogs.actions.show(dialogs.dialogTypes.B2C_DOMAIN_PURCHASE, {
              triggerId: triggerIds.PUBLISH_INTERRUPTION,
              shouldPublishOnSuccess: true,
              withFreeSubdomain: true,
              withPurchasedDomains: true,
              premiumFeature: PUBLISHING,
              captionsOverrides: {
                closeButton: i18next.t('Back'),
                title: i18next.t('Choose Your Domain'),
              },
            })
          );

          next(action);
          break;
        }

        if (isCustomDomainWithoutSubscription) {
          publishingUpsell
            .onUpgrade({
              disabledPlans: [B2C_HOSTING_FREE],
              successUrl: getPublishUrl(),
              isReactivatingSubscription: true,
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Upgrade flow failed or was canceled:', error);
            });

          next(action);
          break;
        }

        store.dispatch(wsDialogs.actions.show(dialogTypes.FREE_TRIAL_PUBLISHING_FLOW_DIALOG));

        next(action);
        break;
      }

      const isPublishUpsellFlowAvailable =
        isPublishUpsellEnabled && (!hasAnyDomainToPublish || isCustomDomainWithoutSubscription);
      const isUpgradeToPublishFlowAvailable =
        isUpgradeToPublishEnabled && (!hasAnyDomainToPublish || !activeHostingSubscription);

      if (isPublishUpsellFlowAvailable || isUpgradeToPublishFlowAvailable) {
        store.dispatch(wsDialogs.actions.show(dialogTypes.PUBLISH_INTERRUPTION_DIALOG));

        next(action);
        break;
      }

      const activeSubscriptions = getActiveSubscriptions(state);

      if (subscriptionManagerVerifiers.hasExpiredPaidSubscription(activeSubscriptions)) {
        store.dispatch(
          dialogs.actions.show(dialogs.dialogTypes.SUBSCRIPTION_EXPIRED, {
            onClose: () => store.dispatch(actions.publishWebsite(domain, isThirdPartyDomain)),
            additionalPaymentParams: {
              successUrl: getPublishUrl(domain),
            },
          })
        );

        next(action);
        break;
      }

      if (isPublishedToCustomDomain && !activeHostingSubscription) {
        publishingUpsell
          .onUpgrade({
            successUrl: getPublishUrl(siteURL),
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Upgrade flow failed or was canceled:', error);
          });

        next(action);
        break;
      }

      store.dispatch(actions.publishWebsite(domain, isThirdPartyDomain));
      next(action);
      break;
    }

    case actionTypes.PUBLISH_WEBSITE: {
      let { vhostName } = action.payload;
      const { isThirdPartyDomain } = action.payload;

      if (vhostName === constants.FREE_YOLA_SUBDOMAIN) {
        vhostName = null;
      }

      const state = store.getState();
      const publishingStatus = selectors.getPublishingStatus(state);

      if (publishingStatus === publishingJobStatuses.IN_PROGRESS) {
        next(action);
        return;
      }

      const siteId = auth.selectors.getSiteId(state);
      const publishingType = user.selectors.getPublishingType(state);

      store.dispatch(
        dialogs.actions.show(dialogs.dialogTypes.PUBLISH_PROGRESS, {
          captions: {
            title: i18next.t('Preparing for launch'),
            description: i18next.t('Your website will be published soon'),
          },
        })
      );
      store.dispatch(actions.setPublishingStatus(publishingJobStatuses.IN_PROGRESS));

      getPublishingJobsInProgress(siteId, publishingType)
        .then((jobs) => {
          if (jobs.length) {
            return pollGetPublishingJob({
              jobId: jobs[0].id,
              onSuccess: (getPublishingJobResponse) =>
                store.dispatch(
                  continuePublishingProgress({
                    response: getPublishingJobResponse,
                    isThirdPartyDomain,
                  })
                ),
              onRequestFail: () =>
                store.dispatch(
                  continuePublishingProgress({
                    response: {
                      data: { status: publishingJobStatuses.FAILED },
                      isThirdPartyDomain,
                    },
                  })
                ),
            });
          }

          return service
            .initiatePublishing(siteId, constants.PUBLISHING_INITIATOR_APP_NAME, vhostName)
            .then(({ data: { id } }) =>
              pollGetPublishingJob({
                jobId: id,
                onRequestFail: () => {
                  store.dispatch(
                    continuePublishingProgress({
                      response: { data: { status: publishingJobStatuses.FAILED } },
                      isThirdPartyDomain,
                      nextCb: () => {
                        next(action);
                      },
                    })
                  );
                },
                onSuccess: (response) =>
                  store.dispatch(
                    thunks.continuePublishingProgress({
                      response,
                      isThirdPartyDomain,
                      nextCb: () => {
                        next(action);
                      },
                    })
                  ),
              })
            );
        })
        .catch(() => {
          store.dispatch(actions.setPublishingStatus(publishingJobStatuses.FAILED));

          store.dispatch(dialogs.actions.hide());
          next(action);
        });

      break;
    }

    case actionTypes.FINISH_PUBLISHING: {
      const {
        siteUrl,
        withUpsell = false,
        isPublished,
        partnerId,
        isThirdPartyDomain,
        hasUntranslatedCustomCode,
      } = action.payload;
      const featureFlagsManager = featureFlagsService.get();
      const state = store.getState();
      const activeSubscriptions = getActiveSubscriptions(state);
      const allSubscriptionsList = getSubscriptionList(state);

      const { freedomain: isFreeDomainEnabled } = await featureFlagsManager.request(['freedomain']);

      store.dispatch(actions.setPublishingStatus(publishingJobStatuses.COMPLETE));
      store.dispatch(dialogs.actions.hide());
      store.dispatch(
        dialogs.actions.show(dialogs.dialogTypes.PUBLISH_SUCCESS, {
          siteUrl,
          withUpsell,
          isPublished,
          partnerId,
          isFreeDomainAvailable: isFreeDomainAvailable(
            activeSubscriptions,
            allSubscriptionsList,
            isFreeDomainEnabled
          ),
          isThirdPartyDomain,
          hasUntranslatedCustomCode,
        })
      );

      next(action);
      break;
    }
    case actionTypes.UNPUBLISH_WEBSITE: {
      const state = store.getState();
      const siteId = auth.selectors.getSiteId(state);
      const { redirectUrl } = action.payload;

      store.dispatch(dialogs.actions.show(dialogs.dialogTypes.UNPUBLISH_PROGRESS));

      service
        .unpublishSite(siteId)
        .then(() => {
          store.dispatch(dialogs.actions.show(dialogs.dialogTypes.UNPUBLISH_SUCCESS));
          store.dispatch(yolaEditorSite.actions.setPublishedSiteData(null));
          store.dispatch(setSettings());

          if (redirectUrl) {
            store.dispatch(dialogs.actions.hide());
            redirectToUrl(redirectUrl);
          }
        })
        .catch((error) => {
          wsDialogs.operations.hide();
          store.dispatch(
            dialogs.actions.show(dialogs.dialogTypes.UNPUBLISH_ERROR, {
              captions: {
                title: i18next.t('Site unpublishing failed'),
                description: i18next.t('Error: {error}', { error: error.message }),
              },
            })
          );
        });
      next(action);
      break;
    }

    case site.actionTypes.RELOAD_SITE_META: {
      next(action);

      const state = store.getState();
      const publishingType = user.selectors.getPublishingType(state);
      const siteId = site.accessors.getSiteId();

      try {
        const jobs = await publishing.helpers.getPublishingJobsInProgress(siteId, publishingType);
        if (jobs.length) {
          await store.dispatch(publishing.thunks.showLastPublishingProgress(jobs[0].id));
        }
      } catch (error) {
        console.error(error);
      }
      break;
    }
    default:
      next(action);
  }
};

export default websitePublisher;
